import {
    keepPreviousData, queryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ApiEndpoint } from '@/constants';
import {
    AllCommentItem, AvailableKeysListResponse,
    ChangelogRelease, DashboardStatsParams, DashboardStatsResponse, DefaultApiParams, DefaultItemResponse, DefaultItemsResponse, Event as DashboardEvent, EventsApiParams, MappedDefaultItemsResponse, MyCommentItem, Objection, Project, Script, ScriptComment, ScriptsApiParams, ScriptWriterStat, ScriptWriterStatsParams, ScriptWriterStatsResponse, SearchApiParams, SearchResponse, SearchResult, Setting, SupportTicket, User,
} from '@/types';
import {
    api, mapItemsResponse,
} from '@/utils';

export const authQueries = {
    checkUser: () =>
        queryOptions<DefaultItemResponse<User>, AxiosError, User>({
            queryKey: ['auth', 'auth:checkUser'],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.CheckUser);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};

export const dashboardQueries = {
    getStats: (params: DashboardStatsParams = {}) =>
        queryOptions<DashboardStatsResponse, AxiosError, DashboardStatsResponse['data']>({
            queryKey: ['dashboard', 'dashboard:stats', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.DashboardStats, { params });
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),

    getScriptWriterStats: (params: ScriptWriterStatsParams = {}) =>
        queryOptions<ScriptWriterStatsResponse | ScriptWriterStat[], AxiosError, ScriptWriterStat[]>({
            queryKey: ['dashboard', 'dashboard:script-writer-stats', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.ScriptWriterStats, { params });
                return response.data;
            },
            select: (data) => {
                if (Array.isArray(data)) {
                    return data;
                }

                return Array.isArray(data.data) ? data.data : [];
            },
            placeholderData: keepPreviousData,
        }),

    getOnlineUsersCount: () =>
        queryOptions<DefaultItemResponse<{ count: number }>, AxiosError, number>({
            queryKey: ['dashboard', 'dashboard:online-users'],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.OnlineUsers);
                return response.data;
            },
            select: (data) => data.data?.count ?? 0,
            placeholderData: keepPreviousData,
        }),

    getChangelog: () =>
        queryOptions<DefaultItemResponse<ChangelogRelease[]>, AxiosError, MappedDefaultItemsResponse<ChangelogRelease>>({
            queryKey: ['dashboard', 'dashboard:changelog'],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.Changelog);
                return response.data;
            },
            select: (data) => ({
                items: data.data,
                total: data.data.length,
                per_page: data.data.length,
                current_page: 1,
            }),
            placeholderData: keepPreviousData,
        }),

    search: (params: SearchApiParams) =>
        queryOptions<SearchResponse, AxiosError, SearchResult[]>({
            queryKey: ['dashboard', 'search', params.search],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.Search, { params });
                return response.data;
            },
            select: (data) => data.data,
            enabled: params.search.length >= 2,
            placeholderData: keepPreviousData,
        }),
};

export const eventsQueries = {
    getAllEvents: (params: EventsApiParams) =>
        queryOptions<DefaultItemsResponse<DashboardEvent>, AxiosError, MappedDefaultItemsResponse<DashboardEvent>>({
            queryKey: ['events', 'events:all', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.Events, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),
};

export const objectionsQueries = {
    getAllObjections: (params: DefaultApiParams) =>
        queryOptions<DefaultItemsResponse<Objection>, AxiosError, MappedDefaultItemsResponse<Objection>> ({
            queryKey: ['objections', 'objections:all', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.Objections, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),

    getOneObjection: (id: string) =>
        queryOptions<DefaultItemResponse<Objection>, AxiosError, Objection>({
            queryKey: ['objections', 'objections:one', id],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.Objections}/${id}`);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};

export const projectsQueries = {
    getAllProjects: (params: DefaultApiParams) =>
        queryOptions<DefaultItemsResponse<Project>, AxiosError, MappedDefaultItemsResponse<Project>>({
            queryKey: ['projects', 'projects:all', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.Projects, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),

    getOneProject: (id: string) =>
        queryOptions<DefaultItemResponse<Project>, AxiosError, Project>({
            queryKey: ['projects', 'projects:one', id],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.Projects}/${id}`);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};

export const scriptsQueries = {
    getAllScripts: (projectId: string, params: ScriptsApiParams) =>
        queryOptions<DefaultItemsResponse<Script>, AxiosError, MappedDefaultItemsResponse<Script>>({
            queryKey: ['scripts', 'scripts:all', projectId, params],
            queryFn: async () => {
                if (!projectId) {
                    throw new Error('Project ID is required');
                }
                const response = await api.get(`${ApiEndpoint.Projects}/${projectId}/scripts`, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),

    getOneScript: (id: string, projectId?: string) =>
        queryOptions<DefaultItemResponse<Script>, AxiosError, Script>({
            queryKey: ['scripts', 'scripts:one', id, projectId],
            queryFn: async () => {
                if (!projectId) {
                    throw new Error('Project ID is required');
                }
                const response = await api.get(`${ApiEndpoint.Projects}/${projectId}/scripts/${id}`);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};

export const usersQueries = {
    getAllUsers: (params: DefaultApiParams) =>
        queryOptions<DefaultItemsResponse<User>, AxiosError, MappedDefaultItemsResponse<User>>({
            queryKey: ['users', 'users:all', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.Users, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),

    getOneUser: (id: string) =>
        queryOptions<DefaultItemResponse<User>, AxiosError, User>({
            queryKey: ['users', 'users:one', id],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.Users}/${id}`);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};

export const managerQueries = {
    getManagerProjects: (params: DefaultApiParams) =>
        queryOptions<DefaultItemsResponse<Project>, AxiosError, MappedDefaultItemsResponse<Project>>({
            queryKey: ['manager-projects', 'manager-projects:all', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.ManagerProjects, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),

    getManagerProject: (id: string, params?: DefaultApiParams) =>
        queryOptions<DefaultItemResponse<Project>, AxiosError, Project>({
            queryKey: ['manager-projects', 'manager-projects:one', id, params],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.ManagerProjects}/${id}`, { params });
                return response.data;
            },
            select: (data) => data.data,
        }),

    getManagerScript: (id: string) =>
        queryOptions<DefaultItemResponse<Script>, AxiosError, Script>({
            queryKey: ['manager-projects', 'manager-projects:script', id],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.ManagerScripts}/${id}`);
                return response.data;
            },
            select: (data) => data.data,
        }),

    getManagerScripts: (projectId: string, params: ScriptsApiParams) =>
        queryOptions<DefaultItemsResponse<Script>, AxiosError, MappedDefaultItemsResponse<Script>>({
            queryKey: ['manager-scripts', projectId, params],
            queryFn: async () => {
                if (!projectId) {
                    throw new Error('Project ID is required');
                }
                const response = await api.get(`${ApiEndpoint.ManagerProjects}/${projectId}/scripts`, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),
};

export const supportTicketsQueries = {
    getAllSupportTickets: (params: DefaultApiParams) =>
        queryOptions<DefaultItemsResponse<SupportTicket>, AxiosError, MappedDefaultItemsResponse<SupportTicket>>({
            queryKey: ['support-tickets', 'support-tickets:all', params],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.SupportTickets, { params });
                return response.data;
            },
            select: (data) => mapItemsResponse(data),
            placeholderData: keepPreviousData,
        }),

    getOneSupportTicket: (id: string) =>
        queryOptions<DefaultItemResponse<SupportTicket>, AxiosError, SupportTicket>({
            queryKey: ['support-tickets', 'support-tickets:one', id],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.SupportTickets}/${id}`);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};

export const myCommentsQueries = {
    getMyComments: (page: number, perPage: number) =>
        queryOptions<DefaultItemsResponse<MyCommentItem>, AxiosError, DefaultItemsResponse<MyCommentItem>>({
            queryKey: ['my-comments', page, perPage],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.MyComments, {
                    params: { current_page: page, per_page: perPage },
                });
                return response.data;
            },
            placeholderData: keepPreviousData,
        }),
};

export const scriptCommentsQueries = {
    getComments: (scriptId: string) =>
        queryOptions<{ data: ScriptComment[]; result: boolean }, AxiosError, ScriptComment[]>({
            queryKey: ['script-comments', scriptId],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.ScriptComments}/${scriptId}/comments`);
                return response.data;
            },
            select: (data) => data.data,
            enabled: !!scriptId,
        }),
};

export const allCommentsQueries = {
    getAll: (page: number, perPage: number, hideRead: boolean) =>
        queryOptions<DefaultItemsResponse<AllCommentItem>, AxiosError, DefaultItemsResponse<AllCommentItem>>({
            queryKey: ['all-comments', page, perPage, hideRead],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.AllComments, {
                    params: { current_page: page, per_page: perPage, hide_read: hideRead },
                });
                return response.data;
            },
            placeholderData: keepPreviousData,
        }),
};

export const settingsQueries = {
    availableKeys: () =>
        queryOptions<AvailableKeysListResponse, AxiosError, AvailableKeysListResponse['data']>({
            queryKey: ['settings', 'settings:available-keys'],
            queryFn: async () => {
                const response = await api.get(ApiEndpoint.SettingsAvailableKeys);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),

    getOneSetting: (key: string) =>
        queryOptions<DefaultItemResponse<Setting>, AxiosError, Setting>({
            queryKey: ['settings', 'settings:one', key],
            queryFn: async () => {
                const response = await api.get(`${ApiEndpoint.Settings}/${encodeURIComponent(key)}`);
                return response.data;
            },
            select: (data) => data.data,
            placeholderData: keepPreviousData,
        }),
};
